import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { t } from "i18next";
import { DateTime } from "luxon";

import { PdfFolderAcceptedIcon } from "src/core/components/pdf/icons/PdfFolderAcceptedIcon";
import { PdfFolderRejectedIcon } from "src/core/components/pdf/icons/PdfFolderRejectedIcon";
import { PdfModulerIcon } from "src/core/components/pdf/icons/PdfModulerIcon";
import { RadarChartPDF } from "src/core/components/pdf/RadarChartPDF";
import { Title } from "src/core/components/pdf/shared/Title";
import { FolderFileType } from "src/core/components/pdf/shared/types";
import { getRiskBackgroundColor, getRiskTextColor } from "src/core/components/pdf/shared/utils";
import { Status } from "src/core/constants/folders";
import { CartographyRisks } from "src/core/contexts/FolderContext";
import { mapRiskLevelToCustomRiskLevel } from "src/core/mappers/risk.mapper";

const styles = StyleSheet.create({
  container: {
    marginTop: 24,
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0A1A53",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#0A1A53",
  },
  sectionsRow: {
    flexDirection: "row",
  },
  leftColumn: {
    width: "60%",
    paddingRight: 8,
  },
  leftZoneBorder: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    paddingBottom: 0,
    marginBottom: 8,
    backgroundColor: "#F5F6F9",
    borderColor: "#E6E8ED",
  },
  rightColumn: {
    width: "40%",
    paddingLeft: 8,
    justifyContent: "flex-start",
    alignItems: "center",
  },
  radarSection: {
    marginTop: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  radarContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    // gap: 4,
    marginBottom: 6,
  },
  labelColumn: {
    flexDirection: "column",
    marginBottom: 6,
  },
  label: {
    fontSize: 11,
    color: "#000",
    marginBottom: 2,
    flexShrink: 0,
  },
  boldLabel: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#000",
    marginBottom: 2,
  },
  value: {
    fontSize: 11,
    lineHeight: 1.4,
  },
  followSection: {
    marginLeft: 12,
  },
  followLabel: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 4,
  },
  followBody: {
    fontSize: 11,
    marginBottom: 2,
  },
  tag: {
    alignSelf: "flex-start",
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 10,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 4,
    height: 24,
  },
  tagText: {
    fontSize: 11,
    color: "#FFFFFF",
  },
  tagIcon: {
    width: 16,
    height: 16,
    marginRight: 5,
  },
  splitRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  splitRowLeft: {
    width: "50%",
    paddingRight: 8,
  },
  splitRowLabel: {
    fontSize: 11,
    marginBottom: 2,
  },
  splitRowLabelTiny: {
    flexDirection: "row",
    alignItems: "center",
    fontSize: 10,
    marginBottom: 2,
  },
  splitRowRight: {
    width: "50%",
    paddingLeft: 8,
  },
  globalRiskInline: {
    marginBottom: 4,
    textAlign: "center",
    fontSize: 11,
  },
});

type SynthesisSectionProps = {
  folderFile: FolderFileType;
  folderRisks: CartographyRisks;
  /** Image PNG du graphe (data URL) si exportée depuis la vue ; sinon rendu SVG. */
  graphImageDataUrl?: string | null;
};

const getRiskLabel = (customRiskLevel: string | undefined): string => {
  if (!customRiskLevel || customRiskLevel === "UNDEFINED") return t("empty");
  const key = `pdf.riskLevel.${customRiskLevel.toLowerCase()}`;
  return t(key);
};

export const SynthesisSection = ({ folderFile, folderRisks }: SynthesisSectionProps) => {
  // >>> Titre de la section : Nom de la personne ou de l'entreprise
  const isPerson = folderFile.fileCustomerFolder?.customer?.isPerson;

  const allInputValues = folderFile.fileCustomerFolder?.customer?.allInputValues ?? [];
  const name = isPerson
    ? allInputValues?.find((inputValue) => inputValue.inputField.fieldCode === "firstName")
        ?.stringValue +
      " " +
      allInputValues?.find((inputValue) => inputValue.inputField.fieldCode === "lastName")
        ?.stringValue
    : allInputValues?.find((inputValue) => inputValue.inputField.fieldCode === "name")?.stringValue;
  // <<< Nom de la personne ou de l'entreprise

  const isForcedRisk = folderFile.riskStatus?.forcedRisk;
  const globalRisk = folderFile.riskStatus?.globalRiskLevel;
  const riskBackgroundColor = getRiskBackgroundColor(mapRiskLevelToCustomRiskLevel(globalRisk));

  const riskStatus = folderFile.riskStatus;
  const reason = riskStatus?.forcedRisk?.reason?.name;
  const hasModulation = !!riskStatus?.forcedRisk;

  const standardRiskLevel = riskStatus?.standardRiskLevel;
  const l2DcRiskColor = getRiskTextColor(mapRiskLevelToCustomRiskLevel(standardRiskLevel));
  const statusCode = folderFile.workflowStatus?.state?.code;

  const owner = folderFile.currentCustomerFolder?.owner;
  const ownerName = [owner?.firstName, owner?.lastName].filter(Boolean).join(" ");

  const validationEvent = folderFile.auditEvents?.find((event) => {
    if (event.__typename === "WorkflowEvent") {
      return event.state.code === Status.VALIDATED;
    }
    return false;
  });
  const validatedBy =
    validationEvent && validationEvent.__typename === "WorkflowEvent"
      ? validationEvent.user?.name ?? null
      : null;
  const validatedDate =
    validationEvent && validationEvent.__typename === "WorkflowEvent" && validationEvent.date
      ? DateTime.fromISO(validationEvent.date).toFormat("dd/MM/yyyy")
      : null;

  const isRejected = folderFile.workflowStatus?.state?.code === Status.REJECTED;

  const customGlobalRisk = mapRiskLevelToCustomRiskLevel(globalRisk);
  const globalRiskTextColor = getRiskTextColor(customGlobalRisk);

  return (
    <View style={styles.container}>
      <Text style={styles.pageTitle}>{name}</Text>

      <Title title={t("pdf.synthesis.title")} />

      <View style={styles.sectionsRow}>
        <View style={styles.leftColumn}>
          <View style={styles.leftZoneBorder}>
            <View style={styles.splitRow}>
              <View style={styles.splitRowLeft}>
                <Text style={styles.splitRowLabel}>{t("pdf.synthesis.vigilanceApplied")}</Text>
                <View style={styles.splitRowLabelTiny}>
                  <Text>{t("pdf.synthesis.vigilanceL2Dc")} </Text>
                  <Text style={{ color: l2DcRiskColor }}>
                    {getRiskLabel(mapRiskLevelToCustomRiskLevel(standardRiskLevel))}
                  </Text>
                </View>
              </View>
              <View style={styles.splitRowRight}>
                {globalRisk && (
                  <View style={{ alignItems: "center" }}>
                    <Text style={[styles.globalRiskInline, { color: globalRiskTextColor }]}>
                      {t("pdf.synthesis.risk")}
                      {t(`pdf.riskLevel.${customGlobalRisk.toLowerCase()}Risk`)}
                    </Text>

                    <View style={[styles.tag, { backgroundColor: riskBackgroundColor }]}>
                      {isForcedRisk && <PdfModulerIcon size={16} style={styles.tagIcon} />}
                      <Text style={styles.tagText}>
                        {t(`pdf.riskLevel.${globalRisk.toLowerCase()}Vigilance`)}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </View>

            {hasModulation && reason && (
              <View style={styles.labelColumn}>
                <Text style={styles.boldLabel}>{t("pdf.synthesis.modulationReason")}</Text>
                <Text style={styles.value} wrap>
                  {reason}
                </Text>
              </View>
            )}

            <View style={styles.splitRow}>
              <View style={styles.splitRowLeft}>
                <Text style={styles.splitRowLabel}>{t("pdf.synthesis.folderStatus")}</Text>
              </View>
              <View style={styles.splitRowRight}>
                {isRejected ? (
                  <View style={[styles.tag, { backgroundColor: "#C95470" }]}>
                    <PdfFolderRejectedIcon size={16} style={styles.tagIcon} />
                    <Text style={styles.tagText}>{t("pdf.status.folderRefused")}</Text>
                  </View>
                ) : (
                  <View style={[styles.tag, { backgroundColor: "#00A8E4" }]}>
                    <PdfFolderAcceptedIcon size={16} style={styles.tagIcon} />
                    <Text style={styles.tagText}>{t("pdf.status.folderAccepted")}</Text>
                  </View>
                )}
              </View>
            </View>
          </View>

          <View style={styles.followSection}>
            <Text style={styles.followLabel}>{t("pdf.followed.title")}</Text>
            {owner && (
              <Text style={styles.followBody}>
                {t("pdf.followed.inChargeOf", { name: ownerName })}
              </Text>
            )}
            {validatedBy && validatedDate && statusCode === Status.VALIDATED && (
              <Text style={styles.followBody}>
                {t("pdf.followed.validatedByWithDate", {
                  name: validatedBy,
                  date: validatedDate,
                })}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.rightColumn}>
          <View style={styles.radarSection}>
            <RadarChartPDF folderRisks={folderRisks} />
          </View>
        </View>
      </View>
    </View>
  );
};
